import {
  Breadcrumbs,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { GridColDef } from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import { FiltersFuncionarios, useFuncionariosStore } from "../../stores/rh/funcionarios";
import { useCentroCustoStore } from "../../stores/comercial/centrocusto";
import { useNavigate } from "react-router";
import { useCargoStore } from "../../stores/rh/cargo";
import { rhAPI } from "../../api";
import { Funcionario } from "../../types/rh/funcionario";
import { AppContext } from "../../common/contexts/app_context/app_context";
import { LegendComponent } from "../../common/components/legend_component";
import { LegendItemComponent } from "../../common/components/legend_item_component";
import { LegendIcon } from "../../common/components/legend_icon";

import RefreshButton from "../../components/global/RefreshButton";
import PrimaryButton from "../../components/global/PrimaryButton";
import DynamicIcon from "../../components/global/DynamicIcon";
import CascateList from "../../components/global/CascateList";
import Input from "../../components/global/Input";
import dayjs from "dayjs";
import CentroCustoMultipleSelect from "../../components/comercial/CentroCustoMultipleSelect";
import dataUtils from "../../common/utils/dataUtils";
import ItemStatus from "../../components/global/ItemStatus";
import AlertErro from "../../components/global/AlertErro";
import DeleteDialog from "../../components/global/DeleteDialog";
import StyledDataGrid from "../../components/global/StyledDataGrid";

import "dayjs/locale/pt-br";

function FuncionarioList() {
  const {
    filters,
    setFilters,
    cleanFilters,
    setFuncionarios,
    loadingFuncionarios,
    funcionarios,
    totalItems,
    setSelectedFuncionarioId,
    selectedFuncionarioId,
    erroFuncionarios,
    messageErroFuncionarios,
    setLoadingFuncionarios,
    isFiltered,
  } = useFuncionariosStore();
  const { setNoticeOpen, setMessageNotice } = useContext(AppContext);
  const { cargoResume, setCargoResume } = useCargoStore();
  const { centroCustoResume, setCentroCustoResume } = useCentroCustoStore();
  const navigate = useNavigate();
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState<Funcionario>();
  const [erroSave, setErroSave] = useState(false);
  const [messageErroSave, setMessageErroSave] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (funcionarios.length === 0 && isFiltered() == false) {
      cleanFilters();
    }
    if (cargoResume.length === 0) {
      setCargoResume();
    }
    if (centroCustoResume.length === 0) {
      setCentroCustoResume();
    }
    setIsInitialized(true);
  }, [funcionarios, cargoResume, cargoResume]);

  const handleReload = async () => {
    setErroSave(false);
    await setCargoResume();
    await setFuncionarios(filters);
    await setCentroCustoResume();
    setMessageNotice("Página recarregada com sucesso!");
    setNoticeOpen(true);
  };

  const handlePaginationChange = async (event: React.ChangeEvent<unknown> | null, page: number) => {
    const updatedFilters: FiltersFuncionarios = {
      ...filters,
      page: page + 1,
    };
    setFilters(updatedFilters);
    await setFuncionarios(updatedFilters);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const updatedFilters: FiltersFuncionarios = {
      ...filters,
      page: 1,
      pageSize: parseInt(event.target.value, 10),
    };
    setFilters(updatedFilters);
    await setFuncionarios(updatedFilters);
  };

  const handleDelete = async () => {
    setErroSave(false);
    setDialogDeleteOpen(false);
    if (selectedFuncionarioId != 0) {
      setLoadingFuncionarios(true);
      var result = await rhAPI.deleteFuncionario(selectedFuncionarioId);
      if (result.statusCode != 200) {
        setErroSave(true);
        setMessageErroSave(result.error?.Message ?? "Ocorreu um erro inesperado.");
        setMessageNotice("Ocorreu algum erro ao tentar deletar.");
        setNoticeOpen(true);
      } else {
        setMessageNotice("Funcionário excluído com sucesso!");
        setNoticeOpen(true);
      }
      await setFuncionarios(filters);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      sortable: false,
      headerClassName: "funcionarios-header",
      renderCell: (params: any) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            title={params.value == 1 ? "A" : params.value == 2 ? "D" : params.value == 3 ? "AF" : "?"}
            color={params.value == 1 ? "green" : params.value == 2 ? "red" : params.value == 3 ? "yellow" : "blue"}
            description={
              params.value == 1
                ? "Funcionário ativo"
                : params.value == 2
                  ? "Funcionário demitido"
                  : params.value == 3
                    ? "Funcionário afastado"
                    : "?"
            }
          />
        </div>
      ),
    },
    {
      field: "nome",
      filterable: false,
      headerName: "Nome",
      flex: 2,
      minWidth: 150,
      sortable: false,
      headerClassName: "funcionarios-header",
    },
    {
      field: "idCargo",
      filterable: false,
      headerName: "Cargo",
      minWidth: 150,
      flex: 2,
      sortable: false,
      headerClassName: "funcionarios-header",
      valueGetter: (value: any) => {
        let cargo = cargoResume.find((x) => x.idCargo == value);
        return cargo ? cargo.descricao : "-";
      },
    },
    {
      field: "idCentroCusto",
      filterable: false,
      headerName: "Centro de Custo",
      minWidth: 150,
      flex: 2,
      sortable: false,
      valueGetter: (value: any) => {
        let cc = centroCustoResume.find((x) => x.idCentroCusto == value);
        return cc ? cc.codigo + " - " + cc.descricao : "-";
      },
      headerClassName: "funcionarios-header",
    },
    {
      field: "dataadmissao",
      filterable: false,
      headerName: "Admissão",
      minWidth: 100,
      flex: 1,
      sortable: false,
      valueGetter: (value: any) => {
        return dataUtils.dataFormatada(value ? value : null);
      },
      headerClassName: "funcionarios-header",
    },
    {
      field: "datademissao",
      filterable: false,
      headerName: "Demissão",
      minWidth: 100,
      flex: 1,
      sortable: false,
      valueGetter: (value: any) => {
        return dataUtils.dataFormatada(value ? value : null);
      },
      headerClassName: "funcionarios-header",
    },
    {
      field: "dataafastamento",
      filterable: false,
      headerName: "Afastamento",
      minWidth: 100,
      flex: 1,
      sortable: false,
      valueGetter: (value: any) => {
        return dataUtils.dataFormatada(value ? value : null);
      },
      headerClassName: "funcionarios-header",
    },
    {
      field: "idFuncionario",
      sortable: false,
      filterable: false,
      headerName: "Ações",
      minWidth: 100,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton
              onClick={() => {
                setSelectedFuncionarioId(params.value);
                navigate("/Funcionarios_cadastro");
              }}
            >
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>
            <IconButton
              onClick={() => {
                setSelectedFuncionario(params.row);
                setSelectedFuncionarioId(params.value);
                setDialogDeleteOpen(true);
              }}
            >
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
      <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
        <Link underline="hover" color="inherit">
          RH
        </Link>
        <Typography className="text-primary-green">Funcionários</Typography>
      </Breadcrumbs>
      <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
        <div className="flex gap-2 items-center w-full">
          <h1 className="text-3xl font-medium text-black dark:text-white">Funcionários</h1>
          <RefreshButton onClick={async () => await handleReload()} />
        </div>
        <div className="w-full justify-end flex">
          <div className="w-[180px] ">
            <PrimaryButton
              onClick={() => {
                setSelectedFuncionarioId(0);
                navigate("/Funcionarios_cadastro");
              }}
              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
            >
              Novo funcionário
            </PrimaryButton>
          </div>
        </div>
      </div>
      {erroFuncionarios == true && (
        <div className="w-full">
          <AlertErro messageErro={messageErroFuncionarios} onTryAgain={async () => await handleReload()} />
        </div>
      )}
      {erroFuncionarios == false && (
        <>
          <CascateList title="Filtros" defaultOpen>
            <div className="w-full flex flex-col gap-6 items-center justify-center">
              <div className="w-full grid gap-6 grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 grid-flow-row-dense ">
                <div className="flex gap-1">
                  <FormControl size="small" sx={{ width: "180px" }}>
                    <InputLabel id="modulo-dashboard">Pesquisar por</InputLabel>
                    <Select
                      labelId="modulo-dashboard"
                      label="Pesquisar por"
                      value={filters.pesquisarPor}
                      onChange={(x) => {
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          pesquisarPor: x.target.value as number,
                        }));
                      }}
                    >
                      <MenuItem value={0}>Nome</MenuItem>
                      <MenuItem value={1}>Cargo</MenuItem>
                      <MenuItem value={2}>Matrícula</MenuItem>
                    </Select>
                  </FormControl>
                  <div className="w-full">
                    <Input
                      variant="outlined"
                      label="Pesquisar"
                      value={filters.pesquisar}
                      onChange={(x) => {
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          pesquisar: x.target.value,
                        }));
                      }}
                      onChangeDebounce={async (value) => {
                        const updatedFilters = {
                          ...filters,
                          pesquisar: value ?? "",
                        };
                        if (isInitialized == true) {
                          await setFuncionarios(updatedFilters);
                        }
                      }}
                    />
                  </div>
                </div>
                <Input
                  variant="outlined"
                  label="Pesquisar por setor"
                  value={filters.pesquisarSetor}
                  onChange={(x) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      pesquisarSetor: x.target.value,
                    }));
                  }}
                  onChangeDebounce={async (value) => {
                    const updatedFilters = {
                      ...filters,
                      pesquisarSetor: value ?? "",
                    };
                    if (isInitialized == true) {
                      await setFuncionarios(updatedFilters);
                    }
                  }}
                />
                <Input
                  variant="outlined"
                  label="Pesquisar por filial"
                  value={filters.pesquisarFilial}
                  onChange={(x) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      pesquisarFilial: x.target.value,
                    }));
                  }}
                  onChangeDebounce={async (value) => {
                    const updatedFilters = {
                      ...filters,
                      pesquisarFilial: value ?? "",
                    };
                    if (isInitialized == true) {
                      await setFuncionarios(updatedFilters);
                    }
                  }}
                />
                <div className="w-full flex gap-1">
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Admissão de"
                    defaultValue={null}
                    value={filters.admissaoDe ? dayjs.utc(filters.admissaoDe as any).local().startOf("day") : null}
                    onChange={async (x) => {
                      const updatedFilters = {
                        ...filters,
                        admissaoDe: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Admissão até"
                    value={filters.admissaoAte ? dayjs.utc(filters.admissaoAte as any).local().startOf("day") : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        admissaoAte: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                </div>
                <div className="w-full flex gap-1">
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Demissão de"
                    value={filters.demissaoDe ? dayjs.utc(filters.demissaoDe as any).local().startOf("day") : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        demissaoDe: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Demissão até"
                    value={filters.demissaoAte ? dayjs.utc(filters.demissaoAte as any).local().startOf("day") : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        demissaoAte: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                </div>
                <div className="w-full flex gap-1">
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Afastamento de"
                    value={filters.afastamentoDe ? dayjs.utc(filters.afastamentoDe as any).local().startOf("day") : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        afastamentoDe: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Afastamento até"
                    value={filters.afastamentoAte ? dayjs.utc(filters.afastamentoAte as any).local().startOf("day") : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        afastamentoAte: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                </div>
                <div className="w-full flex gap-1">
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Férias de"
                    value={filters.feriasDe ? dayjs(filters.feriasDe) : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        feriasDe: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                  <DatePicker
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                    label="Férias até"
                    value={filters.feriasAte ? dayjs(filters.feriasAte) : null}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        feriasAte: x?.isValid() ? x.toISOString() : "",
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  />
                </div>
                <CentroCustoMultipleSelect
                  value={filters.centroCusto}
                  onChange={async (value) => {
                    const updatedFilters: FiltersFuncionarios = {
                      ...filters,
                      centroCusto: value,
                    };
                    setFilters(updatedFilters);
                    if (isInitialized == true) {
                      await setFuncionarios(updatedFilters);
                    }
                  }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="modulo-dashboard">Status</InputLabel>
                  <Select
                    labelId="modulo-dashboard"
                    label="Status"
                    value={filters.status}
                    onChange={async (x) => {
                      const updatedFilters: FiltersFuncionarios = {
                        ...filters,
                        status: x.target.value as number,
                      };
                      setFilters(updatedFilters);
                      if (isInitialized == true) {
                        await setFuncionarios(updatedFilters);
                      }
                    }}
                  >
                    <MenuItem value={0}>Todos os funcionários</MenuItem>
                    <MenuItem value={1}>Ativos</MenuItem>
                    <MenuItem value={2}>Demitidos</MenuItem>
                    <MenuItem value={3}>Afastados</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="w-full flex justify-end">
                <div className="w-[180px]">
                  <PrimaryButton
                    startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
                    buttonColor="yellow"
                    onClick={() => {
                      cleanFilters();
                      setMessageNotice("Os filtros foram limpos com sucesso!");
                      setNoticeOpen(true);
                    }}
                  >
                    Limpar filtros
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </CascateList>
          {erroSave == true && (
            <div className="w-full">
              <AlertErro
                messageErro={messageErroSave}
                onTryAgain={async () => setDialogDeleteOpen(true)}
                onClose={() => {
                  setErroSave(false);
                }}
              />
            </div>
          )}
          <Stack spacing={0}>
            <div className="w-full flex flex-row justify-between items-center">
              <LegendComponent>
                <LegendItemComponent icon={<LegendIcon color="green" title="A" />} label="Ativo" />

                <LegendItemComponent icon={<LegendIcon color="red" title="B" />} label="Demitido" />

                <LegendItemComponent icon={<LegendIcon color="yellow" title="AF" />} label="Afastado" />
              </LegendComponent>

              <TablePagination
                component="div"
                count={totalItems}
                page={filters.page - 1}
                onPageChange={handlePaginationChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                rowsPerPage={filters.pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>

            <StyledDataGrid
              getRowId={(row: any) => row.idFuncionario}
              columns={columns}
              loading={loadingFuncionarios}
              rows={funcionarios}
              rowCount={totalItems}
              pageSizeOptions={[10, 25, 50, 100]}
              paginationMode="server"
              paginationModel={{
                page: filters.page - 1,
                pageSize: filters.pageSize,
              }}
            />
            <TablePagination
              component="div"
              count={totalItems}
              page={filters.page - 1}
              onPageChange={handlePaginationChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              rowsPerPage={filters.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Stack>
        </>
      )}
      <DeleteDialog
        open={dialogDeleteOpen}
        onClose={() => setDialogDeleteOpen(false)}
        title={`Você realmente deseja excluir o(a) funcionário(a) ${selectedFuncionario?.nome ?? ""}?`}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default FuncionarioList;
